import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { buildDetailTabsPostRequest } from '../../domain/detail/detailTabsManagementUseCase';
import {
  getDetailHeader, getDetailTabs, postDetailTabs,
} from './detailService';

export const DETAIL_TABS_QUERY_KEY = 'detailTabs';

/**
 * Mutation key of detail tabs saves for a context (used to know if saves are still in flight)
 * @param {string} contexte - The context
 * @returns {string[]} Mutation key
 */
export const getDetailTabsMutationKey = (contexte) => [DETAIL_TABS_QUERY_KEY, 'save', contexte];

/**
 * Get detail header data
 * @param {string} path - The path of the page
 * @param {string} contexte - The context
 * @param {object} [params = {}] - The query params
 * @param {QueriesOptions} [options = {}] - The query options
 * @returns {import('@tanstack/react-query').UseQueryResult<any, Error>} Detail header data
 */
export const useGetDetailHeader = (path, contexte, params = {}, options = {}) => useQuery({
  queryKey: ['detailHeader', path, contexte, params],
  queryFn: () => getDetailHeader(path, contexte),
  placeholderData: keepPreviousData,
  ...options,
});

/**
 * Get detail tabs data
 * @param {string} path - The path of the page
 * @param {string} contexte - The context
 * @param {object} [params = {}] - The query params
 * @param {QueriesOptions} [options = {}] - The query options
 * @returns {import('@tanstack/react-query').UseQueryResult<DetailTabsResponse, Error>} Detail tabs data
 */
export const useGetDetailTabs = (path, contexte, params = {}, options = {}) => useQuery({
  queryKey: [DETAIL_TABS_QUERY_KEY, path, contexte, params],
  queryFn: () => getDetailTabs(path, contexte),
  placeholderData: keepPreviousData,
  ...options,
});

/**
 * Post detail tabs configuration
 * @param {string} path - The path of the page
 * @param {string} contexte - The context
 * @param {import('@tanstack/react-query').UseMutationOptions<DetailTabsResponse, Error, { nextTabs: DetailTabItem[], optimistic?: boolean }, unknown>} [options = {}] - The mutation options
 * @returns {import('@tanstack/react-query').UseMutationResult<DetailTabsResponse, Error, { nextTabs: DetailTabItem[], optimistic?: boolean }, unknown>} Detail tabs mutation
 */
export const useDetailTabsMutation = (path, contexte, options = {}) => useMutation({
  mutationKey: getDetailTabsMutationKey(contexte),
  // Saves send the full tabs tree: run them one after another so the server keeps the last state
  scope: { id: `${DETAIL_TABS_QUERY_KEY}-${contexte}` },
  mutationFn: ({ nextTabs }) => postDetailTabs(path, contexte, buildDetailTabsPostRequest(nextTabs)),
  ...options,
});
